import React, { useMemo } from "react";
import AnalysisPanel, { PanelEmpty } from "../AnalysisPanel";
import Pagination from "../../shared/Pagination";
import useClientPagination from "../../../hooks/useClientPagination";
import { getCiReportPriceGaps } from "../../../api/competitiveIntelligence";
import ThinScroll from "../../shared/ThinScroll";
import { PanelTakeaway, fmtDateShort, fmtRate } from "../shared";

const PAGE_SIZE = 15;

const fmtGap = (abs, pct) => {
  if (abs == null) return "—";
  const value = Number(abs);
  const sign = value > 0 ? "+" : value < 0 ? "−" : "";
  const pctStr = pct == null ? "" : ` (${pct > 0 ? "+" : pct < 0 ? "−" : ""}${Math.abs(Number(pct)).toFixed(1)}%)`;
  return `${sign}$${Math.abs(value).toFixed(2)}${pctStr}`;
};

const POSITION_STYLES = {
  UNDERPRICED: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
  COMPETITIVE: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  PREMIUM: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
};

const PositionBadge = ({ position }) => {
  const cls = POSITION_STYLES[position] || "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-wide font-semibold ${cls}`}>
      {position || "—"}
    </span>
  );
};

// Diverging gap scale per spec §3A: green above market, red below, neutral within ±5%.
// (Was previously blue >5% / green within ±5%, which clashed with the green COMPETITIVE
// badge and diverged from RateCalendarPanel's gapTone on the same thresholds.)
const GAP_TONE = (pct) => {
  if (pct == null) return "text-gray-500 dark:text-gray-400";
  if (pct > 5) return "text-emerald-600 dark:text-emerald-400";
  if (pct < -5) return "text-red-600 dark:text-red-400";
  return "text-gray-600 dark:text-gray-400";
};

const PriceGapsBody = ({ rows }) => {
  if (!rows.length) {
    return <PanelEmpty message="No price gap data for the selected category." />;
  }
  return (
    <ThinScroll className="overflow-x-auto responsive-card-table-wrapper">
      <table className="w-full min-w-[1100px] text-sm table-fixed responsive-card-table">
        <colgroup>
          <col style={{ width: "9%" }} />
          <col style={{ width: "22%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "9%" }} />
          <col style={{ width: "16%" }} />
          <col style={{ width: "17%" }} />
        </colgroup>
        <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-4 py-2.5 text-left font-semibold first:pl-5">Date</th>
            <th className="px-4 py-2.5 text-left font-semibold">Site Type</th>
            <th className="px-4 py-2.5 text-right font-semibold">Your Rate</th>
            <th className="px-4 py-2.5 text-right font-semibold">Market Avg</th>
            <th className="px-4 py-2.5 text-right font-semibold">Market Min</th>
            <th className="px-4 py-2.5 text-right font-semibold">Market Max</th>
            <th className="px-4 py-2.5 text-right font-semibold">Gap vs Avg</th>
            <th className="px-4 py-2.5 text-left font-semibold last:pr-5">Position</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={`${row.check_in}-${row.your_site_type}-${row.date_window_name || index}`} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
              <td data-label="Date" className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap first:pl-5">
                <div>
                  <div>{fmtDateShort(row.check_in)}</div>
                  {typeof row.days_out === "number" && (
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">{row.days_out}d out</div>
                  )}
                </div>
              </td>
              <td data-label="Site Type" className="px-4 py-2.5 text-gray-700 dark:text-gray-300">
                <div>
                  <div className="truncate max-w-[260px]" title={row.your_site_type || ""}>
                    {row.your_site_type || "—"}
                  </div>
                  {row.date_window_name && (
                    <div className="text-[11px] text-gray-500 dark:text-gray-400">{row.date_window_name}</div>
                  )}
                </div>
              </td>
              <td data-label="Your Rate" className="px-4 py-2.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtRate(row.your_rate)}</td>
              <td data-label="Market Avg" className="px-4 py-2.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtRate(row.market_avg)}</td>
              <td data-label="Market Min" className="px-4 py-2.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtRate(row.market_min)}</td>
              <td data-label="Market Max" className="px-4 py-2.5 text-right text-gray-500 dark:text-gray-400 whitespace-nowrap">{fmtRate(row.market_max)}</td>
              <td data-label="Gap vs Avg" className={`px-4 py-2.5 text-right font-semibold whitespace-nowrap ${GAP_TONE(row.gap_pct)}`}>
                {fmtGap(row.gap_vs_avg, row.gap_pct)}
              </td>
              <td data-label="Position" className="px-4 py-2.5 whitespace-nowrap last:pr-5">
                <PositionBadge position={row.position} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </ThinScroll>
  );
};

const PriceGapsBodyPaginated = ({ rows }) => {
  const sorted = useMemo(
    () => [...rows].sort((rowA, rowB) => (rowA.check_in || "").localeCompare(rowB.check_in || "")),
    [rows]
  );
  const { page, setPage, pageItems, totalPages, totalItems } = useClientPagination(sorted, { pageSize: PAGE_SIZE });
  return (
    <>
      <PriceGapsBody rows={pageItems} />
      {totalPages > 1 && (
        <div className="px-5 pt-1 pb-3 border-t border-gray-200 dark:border-gray-800">
          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            totalItems={totalItems}
            perPage={PAGE_SIZE}
            label="rows"
          />
        </div>
      )}
    </>
  );
};

const PriceGapsPanel = ({ clientId, categoryCode, say }) => (
  <AnalysisPanel
    id={`price-gaps-${clientId}-${categoryCode || "all"}`}
    title="Price Gaps"
    tip="For every date and site type: your rate vs the market avg, min, and max. The POSITION column labels each row as Underpriced, Competitive, or Premium so you can scan for outliers."
    description="Per-date, per-site-type comparison of your rate vs market avg/min/max with positioning verdict."
    badge={categoryCode ? categoryCode.toUpperCase() : null}
    fetcher={() => getCiReportPriceGaps(clientId, { categoryCode: categoryCode || "rv" })}
    deps={[clientId, categoryCode]}
  >
    {({ data }) => {
      const rows = Array.isArray(data) ? data : (data?.items ?? []);
      return (
        <>
          <PanelTakeaway panelKey="price_gaps" say={say} />
          <PriceGapsBodyPaginated rows={rows} />
        </>
      );
    }}
  </AnalysisPanel>
);

export default PriceGapsPanel;
